import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Gauge, Hourglass, Timer } from 'lucide-react';
import { dashboardAPI } from '../../services/api';

const days = (value) => (value === null || value === undefined ? '—' : `${value} d`);

const Tile = ({ label, value, sub, icon: Icon, testId }) => (
  <div className="rounded-xl border border-slate-200/70 bg-white px-4 py-3" data-testid={testId}>
    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
      <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />{label}
    </div>
    <p className="mt-1.5 font-mono text-2xl font-bold tabular-nums text-slate-900">{value}</p>
    {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
  </div>
);

export const CycleTimePanel = ({ timeRange = '90d' }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await dashboardAPI.cycleTime({ time_range: timeRange });
      setData(response.data);
      setError(false);
    } catch (err) {
      setError(true);
    }
  }, [timeRange]);

  useEffect(() => { load(); }, [load]);

  if (error) return null;
  if (!data) {
    return <div className="h-28 animate-pulse rounded-2xl border border-slate-200/70 bg-white" data-testid="cycle-time-loading" />;
  }

  const maxStage = Math.max(...data.stages.map((stage) => stage.average || 0), 1);
  const maxBucket = Math.max(...data.ageing_buckets.map((bucket) => bucket.count), 1);

  return (
    <section className="space-y-4 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04)]" data-testid="cycle-time-panel">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="font-heading text-lg font-bold text-slate-900">Inquiry turnaround</h2>
          <p className="text-xs text-slate-500">
            Derived from existing records · {data.inquiries_analysed} inquiries · working days, Sundays excluded
          </p>
        </div>
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
          {data.open_inquiries} still open
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile
          label="Avg to quote" icon={Timer} testId="cycle-avg-to-quote"
          value={days(data.days_to_quote.average)}
          sub={`median ${days(data.days_to_quote.median)} · ${data.days_to_quote.count} quoted`}
        />
        <Tile
          label="Avg to PO" icon={Clock} testId="cycle-avg-to-po"
          value={days(data.days_to_po.average)}
          sub={`${data.days_to_po.count} inquiries reached a PO`}
        />
        <Tile
          label="Closed cycle" icon={Hourglass} testId="cycle-closed-total"
          value={days(data.total_cycle_closed.average)}
          sub={`${data.total_cycle_closed.count} closed · longest ${days(data.total_cycle_closed.max)}`}
        />
        <Tile
          label="Slowest stage" icon={Gauge} testId="cycle-slowest-stage"
          value={data.slowest_stage ? days(data.slowest_stage.average) : '—'}
          sub={data.slowest_stage ? data.slowest_stage.label : 'Not enough data yet'}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-2" data-testid="cycle-stage-breakdown">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Average time per stage</p>
          {data.stages.map((stage) => (
            <div key={stage.key} className="space-y-1" data-testid={`cycle-stage-${stage.key}`}>
              <div className="flex items-baseline justify-between text-xs">
                <span className="text-slate-600">{stage.label}</span>
                <span className="font-mono tabular-nums text-slate-900">
                  {days(stage.average)} <span className="text-slate-400">({stage.count})</span>
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-blue-500 transition-[width] duration-500"
                  style={{ width: `${((stage.average || 0) / maxStage) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-2" data-testid="cycle-ageing-buckets">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Open inquiries — time in current stage</p>
          {data.ageing_buckets.map((bucket) => (
            <div key={bucket.key} className="space-y-1" data-testid={`cycle-bucket-${bucket.key}`}>
              <div className="flex items-baseline justify-between text-xs">
                <span className="text-slate-600">{bucket.label}</span>
                <span className="font-mono tabular-nums text-slate-900">{bucket.count}</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-slate-400 transition-[width] duration-500"
                  style={{ width: `${(bucket.count / maxBucket) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {data.oldest_open.length > 0 && (
        <div className="space-y-2" data-testid="cycle-oldest-open">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Longest waiting right now</p>
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-200/70">
            {data.oldest_open.slice(0, 5).map((row) => (
              <button
                key={row.inquiry_id} type="button"
                onClick={() => navigate(`/inquiries/${row.inquiry_id}`)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors duration-150 hover:bg-blue-50/40"
                data-testid={`cycle-oldest-${row.inquiry_number}`}
              >
                <span className="font-mono text-xs font-semibold text-slate-900">{row.inquiry_number}</span>
                <span className="flex-1 truncate text-xs text-slate-500">{row.customer_name}</span>
                <span className="text-xs text-slate-500">{row.current_stage_label}</span>
                <span className="font-mono text-xs font-bold tabular-nums text-slate-900">{days(row.days_in_current_stage)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {data.logged_effort.total_hours > 0 && (
        <p className="text-xs text-slate-500" data-testid="cycle-logged-effort">
          Logged effort: {data.logged_effort.total_hours} h across {data.logged_effort.inquiries_with_logs} inquiries
          {data.logged_effort.average_hours ? ` · avg ${data.logged_effort.average_hours} h` : ''}
        </p>
      )}
    </section>
  );
};

export default CycleTimePanel;
